import {
  AkNavigationItemLink,
  AkNavMenuItem,
  AkNavMenuRelated,
  AkNavMenuMeta,
  AkNavigationItemGroup,
  AkNavMenuData,
  AkNavigationItem,
  AkNavMenuChild,
  AkNavMenuType,
} from '@ankorstore/design-system';

// `sectionGroups` is added to AkNavigationItem in the design-system; declared
// locally so the monolith compiles ahead of the DS version bump.
type WithSectionGroups = { sectionGroups?: AkNavigationItemGroup[] };

// Custom styling fields returned by the API but not yet typed in the DS data interfaces
type CustomStylingFields = {
  roundedColor?: string | null;
  logoColor?: string | null;
  backgroundRight?: string | null;
  bannerImageUrl?: string | null;
  bannerBadgeText?: string | null;
  bannerBadgeIcon?: string | null;
  bannerBadgeColor?: string | null;
  bannerTitle?: string | null;
  bannerDescription?: string | null;
  bannerDescriptionHighlight?: string | null;
  bannerCta?: string | null;
  bannerCtaDate?: string | null;
  bannerEnabled?: boolean;
  bannerCtaText?: string | null;
  bannerCtaLink?: string | null;
  bannerTitleColor?: string | null;
};

export interface NavItemBannerData {
  imageUrl: string | null;
  badgeText: string | null;
  badgeIcon: string | null;
  badgeColor: string | null;
  title: string | null;
  description: string | null;
  descriptionHighlight: string | null;
  description2: string | null;
  descriptionHighlight2: string | null;
  ctaText: string | null;
  ctaLink: string | null;
  titleColor: string | null;
}

export const getCategoryItem = (item: AkNavMenuItem): AkNavigationItemLink => {
  const attrs = item?.attributes as typeof item.attributes & CustomStylingFields;
  return {
    label: attrs.label || '',
    url: attrs.url || '',
    target: attrs.target || 'self',
    external: attrs.external || false,
    logo: attrs.logo || null,
    roundedColor: attrs.roundedColor || null,
    logoColor: attrs.logoColor || null,
    backgroundRight: attrs.backgroundRight || null,
    highlight: attrs.highlight || false,
    highlightColor: attrs.highlightColor || null,
    meta: item.meta || {},
  } as AkNavigationItemLink;
};

export const getRelatedLinks = (related: AkNavMenuRelated, meta?: AkNavMenuMeta) => {
  const topSectionLinks = [];
  const bottomSectionLinks = [];
  if (related) {
    for (const key in related) {
      const link = related[key] as typeof related[string] & CustomStylingFields;
      const { label, url, target, external, logo } = link;
      const linkData: AkNavigationItemLink = {
        label,
        logo,
        url,
        target,
        external,
        roundedColor: link.roundedColor || null,
        logoColor: link.logoColor || null,
        backgroundRight: link.backgroundRight || null,
        highlight: link.highlight || false,
        highlightColor: link.highlightColor || null,
      } as unknown as AkNavigationItemLink;
      if (meta) {
        const relatedId = meta?.id_category || '';
        const relatedName = link.trackingEvent || '';
        linkData.meta = {
          depth: meta.depth + 1,
          position: meta.position || 0,
          id_category: relatedId,
          name_category: relatedName,
        };
      }
      if (key === 'self') {
        topSectionLinks.push(linkData);
      } else {
        bottomSectionLinks.push(linkData);
      }
    }
  }
  return {
    topSectionLinks,
    bottomSectionLinks,
  };
};

type groupType = {
  leftSectionGroup?: AkNavigationItemGroup;
  rightSectionGroup?: AkNavigationItemGroup;
};

export const getCategoriesGroup = (categories: AkNavMenuChild[] | AkNavigationItemLink[], parentEvent?: string): groupType => {
  const groups: groupType = {};
  let children: AkNavigationItemLink[] = [];
  categories.forEach((category, index) => {
    const { attributes, type, meta } = category;
    if (attributes.children) {
      children = attributes.children?.map((item) => {
        const name_category =
          index === 0 ? item.attributes?.relatedEntity : item.attributes?.trackingEvent || item.attributes?.relatedEntity;
        const id_category = item.attributes.relatedEntityId;
        const { external, label, meta, target, url, logo } = getCategoryItem(item as unknown as AkNavMenuItem);
        const childrenList = item.attributes.children
          ? item.attributes.children.map((child) => {
              const id_category = child.attributes.relatedEntityId;
              const name_category =
                child.attributes.relatedEntity === 'brand'
                  ? `${parentEvent} > ${child.attributes.trackingEvent}`
                  : child.attributes.relatedEntity;
              const { external, label, meta, target, url, logo } = getCategoryItem(child as unknown as AkNavMenuItem);
              return {
                logo,
                external,
                label,
                meta: {
                  ...meta,
                  id_category: id_category,
                  name_category: name_category,
                },
                target,
                url,
              };
            })
          : [];
        const related = getRelatedLinks(item.attributes.related, {
          ...meta,
          name_category: item.attributes.trackingEvent,
          id_category: item.attributes.relatedEntityId,
        });
        return {
          external,
          label,
          logo,
          target,
          url,
          meta: { ...meta, name_category, id_category },
          ...(item.attributes.children && {
            children: childrenList,
          }),
          ...(item.attributes.related && related),
        };
      });
    } else {
      children = categories.map((item) => {
        const { external, label, meta, target, url, logo } = getCategoryItem(item as unknown as AkNavMenuItem);
        return {
          external,
          logo,
          label,
          target,
          url,
          meta: {
            ...meta,
            name_category: item.attributes.relatedEntity,
            id_category: item.attributes.trackingEvent,
          },
        };
      });
    }
    if (index === 0) {
      groups.leftSectionGroup = {
        title: attributes.title,
        type: type,
        meta: meta,
        children: children,
      };
    } else if (attributes.children) {
      groups.rightSectionGroup = {
        title: attributes.title,
        type: type,
        meta: meta,
        children: children,
      };
    }
  });
  return groups;
};

// Map `nav-item-group` children into thematic section groups: each group's
// title becomes a non-clickable bold header, and its children become the links
// rendered underneath it.
export const getSectionGroups = (children: AkNavMenuChild[]): AkNavigationItemGroup[] => {
  return children
    .filter((child) => child.type === AkNavMenuType.NAV_ITEM_GROUP)
    .map((group) => ({
      title: group.attributes.title,
      type: group.type,
      meta: group.meta,
      children: (group.attributes.children ?? []).map((item) =>
        getCategoryItem(item as unknown as AkNavMenuItem)
      ),
    }));
};

export const prepareDataForAkNavBar = (menuData: AkNavMenuData[]): AkNavigationItem[] => {
  return menuData.map((menuItem) => {
    const { attributes: rawAttributes, meta } = menuItem;
    const attributes = rawAttributes as typeof rawAttributes & CustomStylingFields;
    const { label, url, target, external, logo, related, children, trackingEvent, relatedEntity, relatedEntityId, roundedColor, logoColor, backgroundRight, highlight, highlightColor, bannerImageUrl, bannerBadgeText, bannerBadgeIcon, bannerBadgeColor, bannerTitle, bannerDescription, bannerDescriptionHighlight, bannerCta, bannerCtaDate, bannerEnabled, bannerCtaText, bannerCtaLink, bannerTitleColor } = attributes;
    const { topSectionLinks, bottomSectionLinks } = related
      ? getRelatedLinks(related, { ...meta, name_category: '', id_category: relatedEntityId })
      : {
          topSectionLinks: undefined,
          bottomSectionLinks: undefined,
        };
    // Thematic grouping applies only to the featured dropdown trigger (e.g.
    // "Sélections"), which has no URL of its own — category items all have a URL
    // and must keep their existing two-column layout, even though they are also
    // built from `nav-item-group` children.
    const isThematicTrigger =
      !url && Boolean(children?.some((child) => child.type === AkNavMenuType.NAV_ITEM_GROUP));
    const sectionGroups = isThematicTrigger ? getSectionGroups(children) : undefined;

    // When rendering thematic groups, skip the legacy left/right layout built
    // from the same children to avoid duplicating them.
    const { leftSectionGroup, rightSectionGroup } =
      children && !sectionGroups
        ? getCategoriesGroup(children, trackingEvent)
        : {
            leftSectionGroup: undefined,
            rightSectionGroup: undefined,
          };
    return {
      label,
      url,
      target,
      external,
      logo: logo || null,
      roundedColor: roundedColor || null,
      logoColor: logoColor || null,
      backgroundRight: backgroundRight || null,
      highlight: highlight || false,
      highlightColor: highlightColor || null,
      meta: { ...meta, name_category: relatedEntity || trackingEvent, id_category: relatedEntityId },
      topSectionLinks,
      bottomSectionLinks,
      leftSectionGroup,
      rightSectionGroup,
      sectionGroups,
      bannerData: bannerEnabled && bannerTitle
        ? {
            imageUrl: bannerImageUrl || null,
            badgeText: bannerBadgeText || null,
            badgeIcon: bannerBadgeIcon || null,
            badgeColor: bannerBadgeColor || null,
            title: bannerTitle || null,
            description: bannerDescription || null,
            descriptionHighlight: bannerDescriptionHighlight || null,
            description2: bannerCta || null,
            descriptionHighlight2: bannerCtaDate || null,
            ctaText: bannerCtaText || null,
            ctaLink: bannerCtaLink || null,
            titleColor: bannerTitleColor || null,
          }
        : null,
    } as AkNavigationItem & { bannerData?: NavItemBannerData | null } & WithSectionGroups;
  });
};
